import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { usePractitioners } from "@/api/hooks/usePractitioners";
import {
  COI_STATUS_BADGE_VARIANT,
  COI_STATUS_LABELS,
  PAYMENT_LANE_LABELS,
  type Practitioner,
} from "@/api/types/practitioner";
import { PractitionerFormDialog } from "./PractitionerFormDialog";

/** Days between today and an ISO date string (positive = in the future). */
function daysUntil(dateStr: string): number {
  const target = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function PractitionersPage() {
  const [formOpen, setFormOpen] = useState(false);
  const { data: practitioners, isLoading, error, refetch } = usePractitioners();

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Practitioners</h1>
        <Button onClick={() => setFormOpen(true)} data-testid="add-practitioner-button">
          Add practitioner
        </Button>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {!practitioners || practitioners.length === 0 ? (
          <EmptyState
            title="No practitioners yet"
            description="Add a practitioner to start booking them into The Aerie."
            action={{ label: "Add practitioner", onClick: () => setFormOpen(true) }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
            <table className="w-full text-sm" data-testid="practitioners-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                  <th className="px-4 py-2">Practitioner</th>
                  <th className="px-4 py-2">Business name</th>
                  <th className="px-4 py-2">COI status</th>
                  <th className="px-4 py-2">COI expiry</th>
                  <th className="px-4 py-2">Payment lane</th>
                  <th className="px-4 py-2">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {practitioners.map((p) => (
                  <PractitionerRow key={p.id} practitioner={p} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      <PractitionerFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}

function PractitionerRow({ practitioner }: { practitioner: Practitioner }) {
  const expiringSoonDays =
    practitioner.coi_status === "expiring_soon" && practitioner.coi_expiry_date
      ? daysUntil(practitioner.coi_expiry_date)
      : null;

  return (
    <tr className="hover:bg-bg-hover" data-testid={`practitioner-row-${practitioner.id}`}>
      <td className="px-4 py-3">
        <Link to={`/practitioners/${practitioner.id}`} className="font-medium text-text-primary hover:underline">
          {practitioner.contact_name}
        </Link>
      </td>
      <td className="px-4 py-3 text-text-secondary">{practitioner.business_name ?? "—"}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={COI_STATUS_BADGE_VARIANT[practitioner.coi_status]}>
            {COI_STATUS_LABELS[practitioner.coi_status]}
          </Badge>
          {expiringSoonDays !== null && (
            <span className="text-xs text-text-muted">
              expires in {expiringSoonDays} day{expiringSoonDays === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-text-secondary">{practitioner.coi_expiry_date ?? "—"}</td>
      <td className="px-4 py-3 text-text-secondary">{PAYMENT_LANE_LABELS[practitioner.payment_lane]}</td>
      <td className="px-4 py-3">
        <Badge variant={practitioner.active ? "success" : "default"}>
          {practitioner.active ? "Active" : "Inactive"}
        </Badge>
      </td>
    </tr>
  );
}
